// @flow
import React, {Component} from 'react';
import {connect} from 'react-redux';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import createDebugger from 'debug';
import Datetime from 'react-datetime';
const debug = createDebugger('app:components:DatePicker'); // eslint-disable-line no-unused-vars
import moment from 'moment';
import Portal from 'react-portal';
import {DoubleDatePickerStyles, DatePickerStyles} from './DatePicker.styles';
import { renderPortal } from 'trustar/dom-utils';

import {
  Icon,
  IconLookup,
} from 'trustar-2.0/components';
import { DefaultTheme } from 'trustar-2.0/themes';
import type { ThemeType } from 'trustar-2.0/types';
// Style
const StyledDateRangeControls = styled.div`  
    display: inline-flex;
    align-items: center;
  ${DatePickerStyles}
`;
import {Button, IconButton} from 'trustar-2.0/components/Button';
import ButtonGroup from 'trustar-2.0/components/Button/ButtonGroup';
import DATE_CONSTANTS from 'trustar-2.0/utils/Date.constants';


// Minor groan, set width outside here so we can calculate absolute positioning
const flyoutWidth = 430;
const halfFlyoutWidth = flyoutWidth / 2;
const StyledDateDoubleDateFlyout = styled.div`
    background: ${({theme}) => theme.ThemeColors.SURFACE.hex};
    position: absolute;
    z-index: 3;
    top: ${props => props.position.top ? (props.position.top - halfFlyoutWidth) + 'px' : '0'};
    left: ${props => props.position.left ? 5 + (props.position.left + halfFlyoutWidth) + 'px' : '80px'};
    box-shadow: 0 1px 10px #4a4a4a;
    padding: 10px;
    border: 1px solid #9b9b9b;
    box-shadow: 0 1px 4px 0 rgba(0,0,0,0.53);
    width: ${flyoutWidth}px;
    ${DoubleDatePickerStyles}

`;

const StyledDatePresets = styled.div`
    display: flex;
    align-items: center;
    position: relative;
`;

const initialValues = {
  initialFrom: new Date(),
  initialTo: new Date(),
  theme: DefaultTheme,
  pastLimit: DATE_CONSTANTS.EPOCH_0,
  futureLimit: DATE_CONSTANTS.NOW,
};

// Bindings
type DatePickerProps = {
  from?: Date,
  to?: Date,
  initialFrom?: Date,
  initialTo?: Date,
  isOpen: boolean,
  onChange: Function,
  caption?: string,
  theme: ThemeType,
  pastLimit?: Date,
  futureLimit?: Date,
};

class DatePicker extends Component {
  props: DatePickerProps;
  static defaultProps = initialValues;

  state = { // Local state for these variables
    from: this.props.initialFrom,
    to: this.props.initialTo,
    isOpen: false,
  };
  container = {
    ref: {}, // FlowBS
    position: {
      top: 0,
      left: 0
    },
  };

//  ----Template----

  render() {
    const {props} = this;

    return (
      <StyledDateRangeControls>
        <div className={'dateRangePicker'} ref={el => { this.container.ref = el; }}>
          <span className={'dateRangeToggle'} style={{fontWeight: this.state.isOpen ? 'bold' : 'normal'}} onClick={this.toggleOpen}>
            <Icon icon={IconLookup.CALENDAR_FULL} />
            <span>{this.props.caption || 'Range'}</span>
          </span>
          {this.state.isOpen && (
            <Portal
              isOpened={this.state.isOpen}
              onClose={() => this.setState({ isOpen: false })}
              closeOnEsc={true}>
              <StyledDateDoubleDateFlyout
                position={this.container.position}
                >
                {/* DoubleDatePicker */}
                <div className={'doubleDatePicker'}>
                  <div className={'from'}>
                    <Datetime
                      value={this.state.from}
                      onChange={this.handleFromChange}
                      timeFormat={false /* Disable time fifeld */}
                      isValidDate={this.isFromDateValid}
                      renderInput={({ className, ...inputProps }) => (
                        <header>
                          <span>From:{' '}</span>
                          <input className={'dateInput'} {...inputProps} />
                        </header>
                    )}
                  />
                  </div>
                  <div className={'to'}>
                    <Datetime
                      value={this.state.to}
                      onChange={this.handleToChange}
                      timeFormat={false /* Disable time fifeld */}
                      isValidDate={this.isToDateValid}
                      renderInput={({ className, ...inputProps }) => (
                        <header>
                          <span>To:{' '}</span>
                          <input className={'dateInput'} {...inputProps} />
                        </header>
                    )}
                  />
                  </div>
                </div>

                <ButtonGroup className={'dateRangeControls'}>
                  <Button size={'small'} fill={'none'} style={{ marginRight: 10 }} onClick={this.onCancel} className={'cancel'}>
                  Cancel
                </Button>
                  <Button size={'small'} onClick={this.handleChange} className={'apply'}>
                  Apply
                </Button>
                </ButtonGroup>
              </StyledDateDoubleDateFlyout>
            </Portal>
          )}
        </div>
        <StyledDatePresets></StyledDatePresets>
      </StyledDateRangeControls>
    );
  }

// ----Lifecycle Hooks----

// ----Methods----

  toggleOpen = () => {
    const {position} = this.container;
    if (this.container.ref && !position.top || !position.left) {
      position.top = this.container.ref.getBoundingClientRect().top;
      position.left = this.container.ref.getBoundingClientRect().left;
    }

    this.setState({ isOpen: !this.state.isOpen });
  }


  onCancel = () => this.setState({ isOpen: false });

  handleFromChange = (x: Object) => this.setState({ from: x.toDate() });

  handleToChange = (x: Object) => this.setState({ to: x.toDate() });


  /**
   * Propagate a change event up to the parent component.
   */
  handleChange = (e: Object) => {
    this.props.onChange({
      from: moment(this.state.from).startOf('day').toDate(),
      to: moment(this.state.to).endOf('day').toDate(),
      e
    });
    this.setState({ isOpen: false });
  };

  isDateWithinRange = (current: Object) => {
    const { pastLimit, futureLimit } = this.props;
    return !current.isBefore(pastLimit) && !current.isAfter(futureLimit);
  };

  isFromDateValid = (current: Object) => {
    const dayBeforeTo = moment(this.state.to).subtract(1, 'day');
    const isOverlapping = current.isAfter(dayBeforeTo);
    return this.isDateWithinRange(current) && !isOverlapping;
  };

  isToDateValid = (current: Object) => {
    const dayAfterFrom = moment(this.state.from).add(1, 'day');
    const isOverlapping = current.isBefore(dayAfterFrom);
    return this.isDateWithinRange(current) && !isOverlapping;
  };
}

// Logic & Helper Components

export {DatePicker};



// WEBPACK FOOTER //
// ./web_modules/trustar-2.0/components/DatePicker/DatePicker.js